import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Paper, Grid, Chip,
  FormControl, InputLabel, Select, MenuItem, Accordion,
  AccordionSummary, AccordionDetails, CircularProgress,
  Autocomplete, IconButton, Tooltip,
} from "@mui/material";
import { ExpandMore, Image as ImageIcon, UploadFile as UploadFileIcon } from "@mui/icons-material";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import CustomTipTapEditor from "../../../customComponents/CustomTipTapEditor";
import PageBlocksBuilder from "./PageBlocksBuilder";
import {
  useGetCmsPageQuery,
  useGetCmsPostQuery,
  useCreateCmsPageMutation,
  useUpdateCmsPageMutation,
  useCreateCmsPostMutation,
  useUpdateCmsPostMutation,
  useGetCmsCategoriesQuery,
} from "../../../app/websiteApiSlice";
import toast from "../../../helpers/toast";

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export default function CmsContentEditor({ type }) {
  // type: "page" | "post"
  const isPost = type === "post";
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  // RTK Query hooks
  const { data: existingPage, isLoading: loadingPage } = useGetCmsPageQuery(id, { skip: !isEditing || isPost });
  const { data: existingPost, isLoading: loadingPost } = useGetCmsPostQuery(id, { skip: !isEditing || !isPost });
  const { data: categories = [] } = useGetCmsCategoriesQuery({ skip: !isPost });

  const [createPage, { isLoading: creatingPage }] = useCreateCmsPageMutation();
  const [updatePage, { isLoading: updatingPage }] = useUpdateCmsPageMutation();
  const [createPost, { isLoading: creatingPost }] = useCreateCmsPostMutation();
  const [updatePost, { isLoading: updatingPost }] = useUpdateCmsPostMutation();

  const [uploadFile, { isLoading: uploadingImage }] = useUploadFileMutation();
  const fileInputRef = useRef(null);

  const isSaving = creatingPage || updatingPage || creatingPost || updatingPost;
  const existing = isPost ? existingPost : existingPage;
  const isLoading = isPost ? loadingPost : loadingPage;

  const [form, setForm] = useState({
    title: "",
    slug: "",
    content: "",
    excerpt: "",
    featured_image: "",
    status: "draft",
    seo_title: "",
    seo_description: "",
    seo_og_image: "",
    category_ids: [],
    page_blocks: [],
  });

  const [slugTouched, setSlugTouched] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        title: existing.title ?? "",
        slug: existing.slug ?? "",
        content: existing.content ?? "",
        excerpt: existing.excerpt ?? "",
        featured_image: existing.featured_image ?? "",
        status: existing.status ?? "draft",
        seo_title: existing.seo_title ?? "",
        seo_description: existing.seo_description ?? "",
        seo_og_image: existing.seo_og_image ?? "",
        category_ids: existing.categories?.map((c) => c.id) ?? [],
        page_blocks: existing.page_blocks ?? [],
      });
      setSlugTouched(true);
    }
  }, [existing]);

  // Auto-generate slug from title if user hasn't manually edited it
  useEffect(() => {
    if (!slugTouched && form.title) {
      setForm((f) => ({ ...f, slug: slugify(f.title) }));
    }
  }, [form.title, slugTouched]);

  const handleContentChange = useCallback(
    (html) => setForm((f) => ({ ...f, content: html })),
    []
  );

  async function handleImageFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await uploadFile(formData).unwrap();
      setForm((f) => ({ ...f, featured_image: res.url }));
    } catch {
      // silent — upload errors are surfaced by the API layer
    }
    e.target.value = "";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.slug.trim()) { toast.error("Slug is required"); return; }

    try {
      if (isEditing) {
        const fn = isPost ? updatePost : updatePage;
        await fn({ id: Number(id), ...form }).unwrap();
        toast.success(`${isPost ? "Post" : "Page"} updated`);
      } else {
        const fn = isPost ? createPost : createPage;
        await fn(form).unwrap();
        toast.success(`${isPost ? "Post" : "Page"} created`);
        navigate(isPost ? "/cms/posts" : "/cms/pages");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to save");
    }
  }

  if (isEditing && isLoading) {
    return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5" fontWeight={700}>
          {isEditing ? `Edit ${isPost ? "Post" : "Page"}` : `New ${isPost ? "Post" : "Page"}`}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={() => navigate(isPost ? "/cms/posts" : "/cms/pages")}
          >
            Cancel
          </Button>
          <Button
            variant={form.status === "published" ? "contained" : "outlined"}
            color="warning"
            onClick={() =>
              setForm((f) => ({
                ...f,
                status: f.status === "published" ? "draft" : "published",
              }))
            }
          >
            {form.status === "published" ? "Revert to Draft" : "Publish"}
          </Button>
          <Button variant="contained" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main content column */}
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Slug"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setForm((f) => ({ ...f, slug: e.target.value }));
              }}
              fullWidth
              required
              size="small"
              helperText={`URL: /${isPost ? "blog" : "p"}/${form.slug || "..."}`}
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
            <PageBlocksBuilder
              blocks={form.page_blocks}
              onChange={(blocks) => setForm((f) => ({ ...f, page_blocks: blocks }))}
            />
          </Paper>

          {/* Posts are prose-first; pages use Rich Text blocks instead */}
          {isPost && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" mb={1}>Content</Typography>
              <CustomTipTapEditor
                key={existing?.id ?? "new"}
                value={form.content}
                onChange={handleContentChange}
                minHeight="400px"
              />
            </Paper>
          )}

          {isPost && (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" mb={1}>Excerpt</Typography>
              <TextField
                value={form.excerpt}
                onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))}
                fullWidth
                multiline
                rows={3}
                placeholder="Short summary shown in the posts list..."
                size="small"
              />
            </Paper>
          )}
        </Grid>

        {/* Sidebar column */}
        <Grid item xs={12} md={4}>
          {/* Status */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" mb={1}>Status</Typography>
            <Chip
              label={form.status === "published" ? "Published" : "Draft"}
              color={form.status === "published" ? "success" : "default"}
              size="small"
            />
          </Paper>

          {/* Categories (posts only) */}
          {isPost && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
              <Typography variant="subtitle2" mb={1}>Categories</Typography>
              <Autocomplete
                multiple
                size="small"
                options={categories}
                getOptionLabel={(o) => o.name}
                value={categories.filter((c) => form.category_ids.includes(c.id))}
                onChange={(_e, val) =>
                  setForm((f) => ({ ...f, category_ids: val.map((c) => c.id) }))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select categories" />
                )}
              />
            </Paper>
          )}

          {/* Featured image */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">
                <ImageIcon fontSize="small" sx={{ verticalAlign: "middle", mr: 0.5 }} />
                Featured Image
              </Typography>
              <Tooltip title="Upload image file">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                  >
                    {uploadingImage
                      ? <CircularProgress size={16} />
                      : <UploadFileIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleImageFileChange}
            />
            <TextField
              value={form.featured_image}
              onChange={(e) => setForm((f) => ({ ...f, featured_image: e.target.value }))}
              fullWidth
              size="small"
              placeholder="Paste URL or upload above"
            />
            {form.featured_image && (
              <Box
                component="img"
                src={form.featured_image}
                alt="preview"
                sx={{ mt: 1, width: "100%", borderRadius: 1, objectFit: "cover", maxHeight: 150 }}
              />
            )}
          </Paper>

          {/* SEO */}
          <Accordion variant="outlined" sx={{ borderRadius: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2">SEO</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TextField
                label="SEO Title"
                value={form.seo_title}
                onChange={(e) => setForm((f) => ({ ...f, seo_title: e.target.value }))}
                fullWidth size="small" sx={{ mb: 1.5 }}
              />
              <TextField
                label="Meta Description"
                value={form.seo_description}
                onChange={(e) => setForm((f) => ({ ...f, seo_description: e.target.value }))}
                fullWidth size="small" multiline rows={3} sx={{ mb: 1.5 }}
              />
              <TextField
                label="OG Image URL"
                value={form.seo_og_image}
                onChange={(e) => setForm((f) => ({ ...f, seo_og_image: e.target.value }))}
                fullWidth size="small"
              />
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
    </Box>
  );
}
